<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useRoute, useRouter } from "vue-router";

import PlotlyWidget from "@/components/datascience/PlotlyWidget.vue";
import ReportMetrics from "@/components/datascience/ReportMetrics.vue";
import VegaWidget from "@/components/datascience/VegaWidget.vue";
import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownButtonItem from "@/components/ui/DropdownButtonItem.vue";
import HtmlSnippetWidget from "@/components/ui/HtmlSnippetWidget.vue";
import ImageWidget from "@/components/ui/ImageWidget.vue";
import RichTextEditorWithActionsAndPreview from "@/components/ui/RichTextEditorWithActionsAndPreview.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import { MediaType, type PresentationItemDto } from "@/dto";
import { RouteNames } from "@/router";
import { useClipboard } from "@/services/clipboard";
import EstimatorReportComparisonPresentationItem from "@/views/comparison/EstimatorReportComparisonPresentationItem.vue";

type PresentationItemProps = Pick<
  PresentationItemDto,
  "id" | "name" | "note" | "media_type" | "value" | "report_id" | "comparison_id"
>;
const { id, name, note, report_id, comparison_id, media_type, value } =
  defineProps<PresentationItemProps>();
const emit = defineEmits<{
  openEstimatorReport: [id: number];
  openComparison: [id: number];
  delete: [id: number];
  saveNote: [id: number, newNote: string];
}>();
const router = useRouter();
const route = useRoute();
const clipboard = useClipboard();
const container = useTemplateRef<HTMLDivElement>("container");
const dropdown = useTemplateRef<HTMLDivElement>("dropdown");
const isEditingNote = ref(false);
const editableNote = ref("");

const tenantName = computed(() => route.params.tenantName as string);
const projectName = computed(() => route.params.projectName as string);
const icon = computed(() => {
  if (
    matchMediaType(media_type, "image/") ||
    matchMediaType(media_type, MediaType.Vega) ||
    matchMediaType(media_type, MediaType.Plotly)
  ) {
    return "icon-bar-chart-2";
  } else {
    return "icon-presentation";
  }
});

function matchMediaType(mediaType: string, match: string) {
  return mediaType.startsWith(match);
}

function onCopyAsImage() {
  // wait until the context menu is hidden
  setTimeout(async () => {
    if (container.value) {
      await clipboard.copyElementAsPng(container.value);
    }
  }, 300);
}

async function onCopyParentLink() {
  if (container.value) {
    const to: { name: string; params: Record<string, number | string> } = {
      name: "",
      params: {
        tenantName: tenantName.value,
        projectName: projectName.value,
      },
    };
    if (matchMediaType(media_type, MediaType.Comparison)) {
      to.name = RouteNames.Comparison;
      to.params.comparisonId = comparison_id!;
    } else {
      to.name = RouteNames.EstimatorReportDetail;
      to.params.reportId = report_id!;
    }
    const route = router.resolve(to);
    const link = new URL(route.href, window.location.origin + window.location.pathname).href;
    await clipboard.copyText(link);
  }
}

function onOpenParent() {
  if (matchMediaType(media_type, MediaType.Comparison)) {
    emit("openComparison", comparison_id!);
  } else {
    emit("openEstimatorReport", report_id!);
  }
}

function onDelete() {
  emit("delete", id);
}

function onEditNote() {
  isEditingNote.value = !isEditingNote.value;
  if (!isEditingNote.value && editableNote.value != note) {
    emit("saveNote", id, editableNote.value);
  }
}

onMounted(() => {
  editableNote.value = note;
});
</script>

<template>
  <div class="presentation-item" ref="container">
    <div class="heading" data-drag-image-selector>
      <div>
        <i class="icon" :class="icon" />
        {{ name }}
      </div>
      <div class="actions">
        <SimpleButton
          :class="{ 'button-highlight': isEditingNote }"
          :icon-left="isEditingNote ? 'icon-check' : 'icon-edit'"
          @click="onEditNote"
          :kind="isEditingNote ? 'primary' : 'tertiary'"
        />
        <SimpleButton
          v-if="!matchMediaType(media_type, MediaType.Comparison)"
          icon-left="icon-external-link"
          @click="onOpenParent"
          kind="tertiary"
        />
        <DropdownButton
          ref="dropdown"
          icon-right="icon-more-vertical"
          kind="tertiary"
          class="remove-from-capture"
        >
          <DropdownButtonItem label="Copy as image" icon-left="icon-copy" @click="onCopyAsImage" />
          <DropdownButtonItem
            :label="
              matchMediaType(media_type, MediaType.Comparison)
                ? 'Copy comparison link'
                : 'Copy report link'
            "
            icon-left="icon-link"
            @click="onCopyParentLink"
          />
          <DropdownButtonItem
            label="Remove from presentation"
            icon-left="icon-trash"
            @click="onDelete"
          />
        </DropdownButton>
      </div>
    </div>
    <div class="value">
      <div class="note">
        <RichTextEditorWithActionsAndPreview
          :editable="isEditingNote"
          v-model:value="editableNote"
        />
      </div>
      <div class="content">
        <ImageWidget
          v-if="matchMediaType(media_type, 'image/')"
          :media-type="media_type"
          :base64-src="value"
        />
        <VegaWidget v-if="matchMediaType(media_type, MediaType.Vega)" :spec="value" />
        <PlotlyWidget v-if="matchMediaType(media_type, MediaType.Plotly)" :spec="value" />
        <HtmlSnippetWidget v-if="matchMediaType(media_type, 'text/html')" :src="value" />
        <ReportMetrics v-if="matchMediaType(media_type, MediaType.Metrics)" :metrics="value" />
        <EstimatorReportComparisonPresentationItem
          v-if="matchMediaType(media_type, MediaType.Comparison)"
          :comparison-id="parseInt(JSON.parse(value).id)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.presentation-item {
  .heading {
    display: flex;
    height: 45px;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--spacing-4);

    .icon {
      padding: var(--spacing-8);
      border-radius: var(--radius-s);
      margin-right: var(--spacing-8);
      background-color: var(--color-button-background-tertiary-hover);
      line-height: calc(var(--font-size-sm) * 1.2);
      vertical-align: middle;
    }

    .actions {
      display: flex;
      flex-direction: row;
      gap: var(--spacing-6);

      .button-highlight {
        border-color: var(--color-button-background-primary);
        box-shadow: 0 0 0 4px rgb(from var(--color-button-background-primary) r g b / 12%);
      }
    }
  }

  .value {
    display: flex;
    flex-direction: column;
    border-radius: var(--radius-s);
    background-color: transparent;
    transition: background-color var(--animation-duration) var(--animation-easing);

    &:has(.editor),
    &:has(.markdown:not(:empty)) {
      background-color: var(--color-background-secondary);
    }

    .note {
      transition: padding var(--animation-duration) var(--animation-easing);

      &:has(.editor) {
        padding: var(--spacing-4);
      }

      :deep(.preview:has(.markdown:not(:empty))) {
        padding: var(--spacing-8);
      }

      :deep(.editor) {
        border-radius: var(--radius-s);
        background-color: var(--color-background-primary);
      }
    }

    .content {
      overflow: hidden;
      padding: var(--radius-m);
      border: solid var(--stroke-width-m) var(--color-stroke-background-primary);
      border-radius: var(--radius-m);
      background-color: var(--color-background-primary);
      container-type: inline-size;

      .note + .content {
        margin: var(--spacing-4);
      }

      &:has(.report-metrics .metrics) {
        padding: 0;
        border: none;

        :deep(.metrics) {
          overflow: hidden;
          border: solid var(--stroke-width-m) var(--color-stroke-background-primary);
          border-radius: var(--radius-m);
        }
      }
    }
  }
}
</style>
